import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { ZERO_CASH, checkMovement, parseCashAmount } from '@aeris/cash'
import type { CashSessionSummary } from '@aeris/contracts'
import {
  MONEY_SCALE,
  formatMoney,
  parseHumanAmount,
  toString,
} from '@aeris/money'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  MoneyInput,
  Select,
  Textarea,
} from '@aeris/ui'

import { ApiError, recordCashMovement } from '../lib/api.ts'

import { AuthorisationDialog } from './authorisation-dialog.tsx'

/**
 * Sangria, suprimento or ajuste.
 *
 * One dialog for the three, because they differ only in a verb, a direction and
 * a permission — three near-identical components would drift apart within a
 * month and the difference would be a bug nobody looks for.
 *
 * What a person types is `1.234,56`; what the server receives is `1234.56`. The
 * translation is @aeris/money's `parseHumanAmount`, deliberately separate from
 * the strict parser that reads machine formats (ADR-0016). At no point does the
 * value pass through a JavaScript number.
 *
 * The "a sangria cannot exceed the drawer" rule is checked here with the same
 * `checkMovement` the server uses — `@aeris/cash` is imported by both, so the
 * two answers cannot drift. That is a courtesy, not the enforcement: the server
 * refuses independently under a row lock, which is the only place the answer
 * can still be true by the time it is acted on.
 *
 * **A sangria do caixa (F249).** Quem não supervisiona registra a sangria com
 * a senha do gerente: o Registrar abre a autorização (ADR-0026) com o valor,
 * e o gerente aprova aquele valor naquele caixa. A senha vale uma vez.
 */

export type CashOperation = 'withdrawal' | 'deposit' | 'adjustment'

const TITLES: Record<CashOperation, string> = {
  withdrawal: 'Sangria',
  deposit: 'Suprimento',
  adjustment: 'Ajustar o caixa',
}

const DESCRIPTIONS: Record<CashOperation, string> = {
  withdrawal:
    'Retirada de dinheiro do caixa. Fica registrada no histórico e não pode ser apagada.',
  deposit:
    'Entrada de dinheiro no caixa, normalmente para troco. Fica registrada no histórico.',
  adjustment:
    'Correção com justificativa. Registrada com o seu nome, e nunca apaga um movimento anterior.',
}

const AMOUNT_LABELS: Record<CashOperation, string> = {
  withdrawal: 'Valor retirado',
  deposit: 'Valor adicionado',
  adjustment: 'Valor do ajuste',
}

export type CashMovementFormProps = {
  operation: CashOperation
  session: CashSessionSummary
  /**
   * Quem está no caixa não supervisiona (F249): a sangria pede a senha do
   * gerente antes de gravar.
   */
  needsAuthorisation?: boolean
  onClose: () => void
}

export const CashMovementForm = ({
  operation,
  session,
  needsAuthorisation = false,
  onClose,
}: CashMovementFormProps): ReactNode => {
  const queryClient = useQueryClient()

  const [amount, setAmount] = useState('')
  const [direction, setDirection] = useState<'in' | 'out'>('out')
  const [note, setNote] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  // A autorização aberta por cima, com o valor que o gerente vai aprovar.
  const [asking, setAsking] = useState(false)

  const authorised = operation === 'withdrawal' && needsAuthorisation

  const expected = parseCashAmount(session.expected) ?? ZERO_CASH

  /**
   * The typed value, exactly.
   *
   * `null` while the field is empty or half-typed, which is a normal state and
   * not an error — so nothing is said about it until the form is submitted.
   */
  const typed =
    amount.trim().length === 0 ? null : parseHumanAmount(amount, MONEY_SCALE)

  // An adjustment is the one operation whose direction the person states; the
  // other two have theirs fixed by the kind, on the server as well as here.
  const effectiveDirection =
    operation === 'adjustment'
      ? direction
      : operation === 'deposit'
        ? 'in'
        : 'out'

  const refusal =
    typed === null
      ? null
      : checkMovement(
          { status: session.status },
          expected,
          operation,
          effectiveDirection,
          typed
        )

  const amountError = ((): string | undefined => {
    if (amount.trim().length === 0) return undefined
    if (typed === null) return 'Informe um valor. Use vírgula para os centavos.'
    if (typed.units <= 0n) return 'O valor deve ser maior que zero.'
    if (refusal !== null && !refusal.ok) {
      return refusal.refusal.reason === 'session-closed'
        ? 'Este caixa já foi fechado.'
        : `Saldo insuficiente no caixa. Disponível: ${formatMoney(expected)}.`
    }
    return undefined
  })()

  const mutation = useMutation({
    mutationFn: async (authorisationId?: string) => {
      if (typed === null) throw new Error('unreachable: guarded by onSubmit')

      return await recordCashMovement(session.id, {
        kind: operation,
        // Sent for every kind; the server overrules it wherever the kind fixes
        // the direction, so this can never turn a sangria into a deposit.
        direction: effectiveDirection,
        // The exact value, in the machine format the contract requires.
        amount: toString(typed),
        note: note.trim().length === 0 ? null : note.trim(),
        ...(authorisationId === undefined ? {} : { authorisationId }),
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cash-session'] })
      /*
       * Closed as soon as the server accepted, and the lists refreshed after.
       *
       * It used to await `invalidateQueries` first, which awaits the refetch of
       * every active query it touches — so the dialog stayed open, with its
       * Salvar button spinning, for as long as an unrelated list screen took to
       * reload. The person's work was finished at the 201; making them watch a
       * background refresh is the dialog reporting somebody else's latency as
       * its own.
       */
      onClose()
      void queryClient.invalidateQueries({ queryKey: ['cash-movements'] })
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? error.message
          : 'Não foi possível registrar. Tente novamente.'
      )
    },
  })

  const onSubmit = (): void => {
    setFormError(null)
    if (typed === null) {
      setFormError('Informe o valor.')
      return
    }
    if (amountError !== undefined) return
    if (authorised) {
      setAsking(true)
      return
    }
    mutation.mutate(undefined)
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={TITLES[operation]}
        description={
          authorised
            ? `${DESCRIPTIONS[operation]} Pede a senha de um gerente.`
            : DESCRIPTIONS[operation]
        }
        className="max-w-md"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault()
            onSubmit()
          }}
          noValidate
          className="flex flex-col gap-4"
        >
          {formError === null ? null : (
            <p
              role="alert"
              className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
            >
              {formError}
            </p>
          )}

          <div className="rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-sunken) px-3 py-2">
            <p className="text-(length:--text-xs) text-(--text-secondary)">
              Saldo esperado agora
            </p>
            <p className="font-(--font-mono) text-(length:--text-lg) font-(--weight-semibold) text-(--text-primary)">
              {formatMoney(expected)}
            </p>
          </div>

          {operation === 'adjustment' ? (
            <Field
              label="Sentido do ajuste"
              hint="Um ajuste é o único movimento que pode ir nos dois sentidos"
            >
              {(field) => (
                <Select
                  {...field}
                  value={direction}
                  onChange={(event) => {
                    setDirection(event.target.value === 'in' ? 'in' : 'out')
                  }}
                >
                  <option value="out">Sai do caixa (faltava dinheiro)</option>
                  <option value="in">Entra no caixa (sobrava dinheiro)</option>
                </Select>
              )}
            </Field>
          ) : null}

          <Field
            label={AMOUNT_LABELS[operation]}
            required
            error={amountError}
            hint="Use vírgula para os centavos"
          >
            {(field) => (
              <MoneyInput
                {...field}
                value={amount}
                onValueChange={setAmount}
                // The field the hands are already on. An operator opening this
                // dialog from the keyboard should type the number, not find it.
                autoFocus
              />
            )}
          </Field>

          <Field
            label="Motivo"
            hint={
              operation === 'adjustment'
                ? 'Recomendado: explique o motivo do ajuste'
                : 'Opcional. Ex.: envio ao cofre'
            }
          >
            {(field) => (
              <Textarea
                {...field}
                rows={2}
                value={note}
                onChange={(event) => {
                  setNote(event.target.value)
                }}
              />
            )}
          </Field>

          <DialogFooter>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
              disabled={amountError !== undefined}
            >
              {authorised ? 'Pedir a senha do gerente' : 'Registrar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {asking && typed !== null ? (
        <AuthorisationDialog
          title="Senha do gerente para a sangria"
          description={`Sangria de ${formatMoney(typed)} do caixa ${session.terminalId}. O gerente digita a senha aqui, e ela vale só para esta sangria.`}
          action="pos.supervise"
          targetType="cash_session"
          targetId={session.id}
          detail={{ kind: 'withdrawal', amount: toString(typed) }}
          onAuthorised={(authorisationId) => {
            setAsking(false)
            mutation.mutate(authorisationId)
          }}
          onClose={() => {
            setAsking(false)
          }}
        />
      ) : null}
    </Dialog>
  )
}
